import React from "react";
import { Box, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { TargetData } from "../types";

interface NightSelectorProps {
  selectedTarget: TargetData | null;
  selectedNight: string | "total";
  nightKeys: string[];
  setSelectedNight: (night: string | "total") => void;
  setTargetLastSelectedNight: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  setEditDialogOpen: (open: boolean) => void;
  confirmDialogRef: React.RefObject<any>;
  setConfirmDialogOpen: (open: boolean) => void;
}

const NightSelector: React.FC<NightSelectorProps> = ({
  selectedTarget,
  selectedNight,
  nightKeys,
  setSelectedNight,
  setTargetLastSelectedNight,
  setEditDialogOpen,
  confirmDialogRef,
  setConfirmDialogOpen,
}) => (
  <Box sx={{ mb: 2.5 }}>
    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      <Button
        variant={selectedNight === "total" ? "contained" : "outlined"}
        onClick={() => {
          if (selectedTarget) {
            setTargetLastSelectedNight((prev) => ({
              ...prev,
              [selectedTarget.id]: "total",
            }));
          }
          setSelectedNight("total");
        }}
        sx={{
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 600,
          backgroundColor:
            selectedNight === "total" ? "rgba(255, 107, 53, 0.06)" : undefined,
          color: selectedNight === "total" ? "primary.main" : undefined,
          transition: "background-color 0.2s, color 0.2s",
          "&:hover": {
            backgroundColor: "rgba(255, 107, 53, 0.07)",
            color: "primary.main",
          },
        }}
      >
        All Nights
      </Button>
      {nightKeys.map((night) => {
        const isSelected = selectedNight === night;
        return (
          <Button
            key={night}
            variant={isSelected ? "contained" : "outlined"}
            onClick={() => {
              if (selectedTarget) {
                setTargetLastSelectedNight((prev) => ({
                  ...prev,
                  [selectedTarget.id]: night,
                }));
              }
              setSelectedNight(night);
            }}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              position: "relative",
              pr: 7,
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              backgroundColor: isSelected
                ? "rgba(255, 107, 53, 0.06)"
                : undefined,
              color: isSelected ? "primary.main" : undefined,
              transition: "background-color 0.2s, color 0.2s",
              "&:hover": {
                backgroundColor: "rgba(255, 107, 53, 0.07)",
                color: "primary.main",
              },
            }}
          >
            {new Date(night).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
            <Box
              component="div"
              sx={{
                position: "absolute",
                right: 25,
                top: "50%",
                transform: "translateY(-50%)",
                color: isSelected ? "#fff" : "text.secondary",
                opacity: 1,
                zIndex: 2,
                p: 0.5,
                cursor: "pointer",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                transition: "color 0.2s, background-color 0.2s",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                },
                "&:active": {
                  backgroundColor: "rgba(255, 255, 255, 0.12)",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedNight(night);
                setEditDialogOpen(true);
              }}
            >
              <EditIcon fontSize="small" />
            </Box>
            <CloseIcon
              fontSize="small"
              sx={{
                position: "absolute",
                right: 6,
                top: "50%",
                transform: "translateY(-50%)",
                color: isSelected ? "#ffb3b3" : "text.secondary",
                cursor: "pointer",
                transition: "color 0.2s",
                pointerEvents: "auto",
                "&:hover": {
                  color: isSelected ? "#ff1744" : "error.main",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (
                  confirmDialogRef.current &&
                  confirmDialogRef.current.showDeleteNight
                ) {
                  confirmDialogRef.current.showDeleteNight(night);
                }
                setConfirmDialogOpen(true);
              }}
            />
          </Button>
        );
      })}
    </Box>
  </Box>
);

export default NightSelector;
